import type { TelemetryEvent } from "./telemetryClient";

/**
 * @param seats
 * @param trial
 */
export class AddSeatsModalConfirmed implements TelemetryEvent {
  readonly eventProperties: {
    seats?: number;
    trial?: boolean;
  };
  readonly eventName = "add_seats_modal_confirmed";

  constructor(props: { seats?: number; trial?: boolean }) {
    this.eventProperties = {
      seats: props.seats ?? undefined,
      trial: props.trial ?? undefined,
    };
  }
}

/** @param location */
export class ApplePayButtonClicked implements TelemetryEvent {
  readonly eventProperties: {
    location: "checkout" | "payment_update_modal";
  };
  readonly eventName = "apple_pay_button_clicked";

  constructor(props: { location: "checkout" | "payment_update_modal" }) {
    this.eventProperties = {
      location: props.location,
    };
  }
}

/** @param location */
export class ApplePaySetupFailed implements TelemetryEvent {
  readonly eventProperties: {
    location: "checkout" | "payment_update_modal";
  };
  readonly eventName = "apple_pay_setup_failed";

  constructor(props: { location: "checkout" | "payment_update_modal" }) {
    this.eventProperties = {
      location: props.location,
    };
  }
}

/**
 * @param fbclid
 * @param gclid
 * @param utmCampaign
 * @param utmMedium
 * @param utmSource
 */
export class CampaignTouched implements TelemetryEvent {
  readonly eventProperties: {
    fbclid?: string;
    gclid?: string;
    utm_campaign?: string;
    utm_medium?: string;
    utm_source?: string;
  };
  readonly eventName = "campaign_touched";

  constructor(props: {
    fbclid?: string;
    gclid?: string;
    utmCampaign?: string;
    utmMedium?: string;
    utmSource?: string;
  }) {
    this.eventProperties = {
      fbclid: props.fbclid ?? undefined,
      gclid: props.gclid ?? undefined,
      utm_campaign: props.utmCampaign ?? undefined,
      utm_medium: props.utmMedium ?? undefined,
      utm_source: props.utmSource ?? undefined,
    };
  }
}

/**
 * @param answerId
 * @param targetUrl
 */
export class ChatbotAnswerLinkClicked implements TelemetryEvent {
  readonly eventProperties: {
    answer_id: string;
    target_url: string;
  };
  readonly eventName = "chatbot_answer_link_clicked";

  constructor(props: { answerId: string; targetUrl: string }) {
    this.eventProperties = {
      answer_id: props.answerId,
      target_url: props.targetUrl,
    };
  }
}

/**
 * @param action
 * @param answerId
 */
export class ChatbotAnswerToolbarClicked implements TelemetryEvent {
  readonly eventProperties: {
    action: "positive" | "negative";
    answer_id: string;
  };
  readonly eventName = "chatbot_answer_toolbar_clicked";

  constructor(props: { action: "positive" | "negative"; answerId: string }) {
    this.eventProperties = {
      action: props.action,
      answer_id: props.answerId,
    };
  }
}

/**
 * @param paymentPlatform
 * @param paymentType
 * @param seats
 * @param trial
 */
export class CheckoutFlowCompleted implements TelemetryEvent {
  readonly eventProperties: {
    payment_platform: "chargebee" | "paddle";
    payment_type?: "apple_pay" | "card";
    seats?: number;
    trial: boolean;
  };
  readonly eventName = "checkout_flow_completed";

  constructor(props: {
    paymentPlatform: "chargebee" | "paddle";
    paymentType?: "apple_pay" | "card";
    seats?: number;
    trial: boolean;
  }) {
    this.eventProperties = {
      payment_platform: props.paymentPlatform,
      payment_type: props.paymentType ?? undefined,
      seats: props.seats ?? undefined,
      trial: props.trial,
    };
  }
}

/**
 * @param paymentPlatform
 * @param reason
 * @param seats
 * @param trial
 */
export class CheckoutFlowFailed implements TelemetryEvent {
  readonly eventProperties: {
    payment_platform: "chargebee" | "paddle";
    reason:
      | "card_authorization_failed"
      | "card_authorization_3ds_failed"
      | "card_payment_failed"
      | "card_payment_3ds_failed"
      | "unknown";
    seats?: number;
    trial: boolean;
  };
  readonly eventName = "checkout_flow_failed";

  constructor(props: {
    paymentPlatform: "chargebee" | "paddle";
    reason:
      | "card_authorization_failed"
      | "card_authorization_3ds_failed"
      | "card_payment_failed"
      | "card_payment_3ds_failed"
      | "unknown";
    seats?: number;
    trial: boolean;
  }) {
    this.eventProperties = {
      payment_platform: props.paymentPlatform,
      reason: props.reason,
      seats: props.seats ?? undefined,
      trial: props.trial,
    };
  }
}

/**
 * @param paymentPlatform
 * @param trial
 * @param type
 */
export class CheckoutFlowIssue implements TelemetryEvent {
  readonly eventProperties: {
    payment_platform: "chargebee" | "paddle";
    trial: boolean;
    type: "invalid_billing_fields" | "vat_validation_failed" | "card_validation_failed";
  };
  readonly eventName = "checkout_flow_issue";

  constructor(props: {
    paymentPlatform: "chargebee" | "paddle";
    trial: boolean;
    type: "invalid_billing_fields" | "vat_validation_failed" | "card_validation_failed";
  }) {
    this.eventProperties = {
      payment_platform: props.paymentPlatform,
      trial: props.trial,
      type: props.type,
    };
  }
}

/** @param reason */
export class CheckoutFlowLoadError implements TelemetryEvent {
  readonly eventProperties: {
    reason:
      | "unsupported_country"
      | "already_has_subscription"
      | "unverified_email"
      | "not_allowed"
      | "unknown";
  };
  readonly eventName = "checkout_flow_load_error";

  constructor(props: {
    reason:
      | "unsupported_country"
      | "already_has_subscription"
      | "unverified_email"
      | "not_allowed"
      | "unknown";
  }) {
    this.eventProperties = {
      reason: props.reason,
    };
  }
}

/** @param paymentPlatform */
export class CheckoutFlowLoaded implements TelemetryEvent {
  readonly eventProperties: {
    payment_platform: "chargebee" | "paddle";
  };
  readonly eventName = "checkout_flow_loaded";

  constructor(props: { paymentPlatform: "chargebee" | "paddle" }) {
    this.eventProperties = {
      payment_platform: props.paymentPlatform,
    };
  }
}

/**
 * @param action
 * @param details
 * @param location
 */
export class ContextMenuActed implements TelemetryEvent {
  readonly eventProperties: {
    action:
      | "edit_in_app"
      | "copy_link"
      | "share"
      | "download"
      | "rename"
      | "view_in_ar"
      | "unpublish";
    details?: string;
    location: "project_page_project_title" | "project_page_version_selector";
  };
  readonly eventName = "context_menu_acted";

  constructor(props: {
    action:
      | "edit_in_app"
      | "copy_link"
      | "share"
      | "download"
      | "rename"
      | "view_in_ar"
      | "unpublish";
    details?: string;
    location: "project_page_project_title" | "project_page_version_selector";
  }) {
    this.eventProperties = {
      action: props.action,
      details: props.details ?? undefined,
      location: props.location,
    };
  }
}

/**
 * @param details
 * @param location
 */
export class ContextMenuOpened implements TelemetryEvent {
  readonly eventProperties: {
    details?: string;
    location: "project_page_project_title" | "project_page_version_selector";
  };
  readonly eventName = "context_menu_opened";

  constructor(props: {
    details?: string;
    location: "project_page_project_title" | "project_page_version_selector";
  }) {
    this.eventProperties = {
      details: props.details ?? undefined,
      location: props.location,
    };
  }
}

/**
 * @param autoOpen
 * @param projectId
 */
export class DeeplinkAutoOpenToggled implements TelemetryEvent {
  readonly eventProperties: {
    auto_open: boolean;
    project_id: string;
  };
  readonly eventName = "deeplink_auto_open_toggled";

  constructor(props: { autoOpen: boolean; projectId: string }) {
    this.eventProperties = {
      auto_open: props.autoOpen,
      project_id: props.projectId,
    };
  }
}

/** @param projectId */
export class DeeplinkButtonClicked implements TelemetryEvent {
  readonly eventProperties: {
    project_id: string;
  };
  readonly eventName = "deeplink_button_clicked";

  constructor(props: { projectId: string }) {
    this.eventProperties = {
      project_id: props.projectId,
    };
  }
}

/** @param projectId */
export class DeeplinkButtonShown implements TelemetryEvent {
  readonly eventProperties: {
    project_id: string;
  };
  readonly eventName = "deeplink_button_shown";

  constructor(props: { projectId: string }) {
    this.eventProperties = {
      project_id: props.projectId,
    };
  }
}

/** @param projectId */
export class DeeplinkGoToDownload implements TelemetryEvent {
  readonly eventProperties: {
    project_id: string;
  };
  readonly eventName = "deeplink_go_to_download";

  constructor(props: { projectId: string }) {
    this.eventProperties = {
      project_id: props.projectId,
    };
  }
}

/** @param projectId */
export class DeeplinkModalClosed implements TelemetryEvent {
  readonly eventProperties: {
    project_id: string;
  };
  readonly eventName = "deeplink_modal_closed";

  constructor(props: { projectId: string }) {
    this.eventProperties = {
      project_id: props.projectId,
    };
  }
}

/** @param projectId */
export class DeeplinkModalOpened implements TelemetryEvent {
  readonly eventProperties: {
    project_id: string;
  };
  readonly eventName = "deeplink_modal_opened";

  constructor(props: { projectId: string }) {
    this.eventProperties = {
      project_id: props.projectId,
    };
  }
}

/**
 * @param autoOpen
 * @param projectId
 */
export class DeeplinkOpenInitiated implements TelemetryEvent {
  readonly eventProperties: {
    auto_open: boolean;
    project_id: string;
  };
  readonly eventName = "deeplink_open_initiated";

  constructor(props: { autoOpen: boolean; projectId: string }) {
    this.eventProperties = {
      auto_open: props.autoOpen,
      project_id: props.projectId,
    };
  }
}

/** @param projectId */
export class DeeplinkRetryButtonClicked implements TelemetryEvent {
  readonly eventProperties: {
    project_id: string;
  };
  readonly eventName = "deeplink_retry_button_clicked";

  constructor(props: { projectId: string }) {
    this.eventProperties = {
      project_id: props.projectId,
    };
  }
}

/**
 * @param action
 * @param details
 * @param elementId
 * @param location
 */
export class ElementClicked implements TelemetryEvent {
  readonly eventProperties: {
    action?: string;
    details?: string;
    element_id?: string;
    location?: string;
  };
  readonly eventName = "element_clicked";

  constructor(props: { action?: string; details?: string; elementId?: string; location?: string }) {
    this.eventProperties = {
      action: props.action ?? undefined,
      details: props.details ?? undefined,
      element_id: props.elementId ?? undefined,
      location: props.location ?? undefined,
    };
  }
}

/**
 * @param action
 * @param details
 * @param location
 */
export class ElementHovered implements TelemetryEvent {
  readonly eventProperties: {
    action?: string;
    details?: string;
    location?: string;
  };
  readonly eventName = "element_hovered";

  constructor(props: { action?: string; details?: string; location?: string }) {
    this.eventProperties = {
      action: props.action ?? undefined,
      details: props.details ?? undefined,
      location: props.location ?? undefined,
    };
  }
}

/**
 * @param action
 * @param details
 * @param location
 */
export class ErrorShown implements TelemetryEvent {
  readonly eventProperties: {
    action?: string;
    details?: string;
    location?: string;
  };
  readonly eventName = "error_shown";

  constructor(props: { action?: string; details?: string; location?: string }) {
    this.eventProperties = {
      action: props.action ?? undefined,
      details: props.details ?? undefined,
      location: props.location ?? undefined,
    };
  }
}

/**
 * @param activeTeamId
 * @param location
 */
export class GoToCollaborationPromo implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    location: "review_links";
  };
  readonly eventName = "go_to_collaboration_promo";

  constructor(props: { activeTeamId?: string; location: "review_links" }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location,
    };
  }
}

/**
 * @param activeTeamId
 * @param destination
 * @param location
 */
export class GoToDashboard implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    destination: "shared_with_me" | "review_links";
    location: "not_found_page" | "request_access_page";
  };
  readonly eventName = "go_to_dashboard";

  constructor(props: {
    activeTeamId?: string;
    destination: "shared_with_me" | "review_links";
    location: "not_found_page" | "request_access_page";
  }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      destination: props.destination,
      location: props.location,
    };
  }
}

/**
 * @param activeTeamId
 * @param location
 */
export class GoToMembers implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    location: "web_dashboard_sidebar";
  };
  readonly eventName = "go_to_members";

  constructor(props: { activeTeamId?: string; location: "web_dashboard_sidebar" }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location,
    };
  }
}

/**
 * @param activeTeamId
 * @param location
 */
export class GoToReviewLinks implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    location: "account_sidebar" | "not_found_page";
  };
  readonly eventName = "go_to_review_links";

  constructor(props: { activeTeamId?: string; location: "account_sidebar" | "not_found_page" }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location,
    };
  }
}

/**
 * @param activeTeamId
 * @param location
 */
export class GoToSharedWithMe implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    location: "account_sidebar" | "not_found_page";
  };
  readonly eventName = "go_to_shared_with_me";

  constructor(props: { activeTeamId?: string; location: "account_sidebar" | "not_found_page" }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location,
    };
  }
}

/**
 * @param activeTeamId
 * @param destination
 * @param location
 * @param role
 */
export class GoToSpace implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    destination: "shared_with_me" | "review_links";
    location: "breadcrumbs";
    role?: "owner" | "editor" | "seat_manager" | "contributor" | "guest";
  };
  readonly eventName = "go_to_space";

  constructor(props: {
    activeTeamId?: string;
    destination: "shared_with_me" | "review_links";
    location: "breadcrumbs";
    role?: "owner" | "editor" | "seat_manager" | "contributor" | "guest";
  }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      destination: props.destination,
      location: props.location,
      role: props.role ?? undefined,
    };
  }
}

/**
 * @param activeTeamId
 * @param destination
 * @param location
 */
export class GoToTeam implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    destination: "shared_with_me" | "review_links";
    location: "breadcrumbs";
  };
  readonly eventName = "go_to_team";

  constructor(props: {
    activeTeamId?: string;
    destination: "shared_with_me" | "review_links";
    location: "breadcrumbs";
  }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      destination: props.destination,
      location: props.location,
    };
  }
}

/** @param trigger */
export class IntentPageFinished implements TelemetryEvent {
  readonly eventProperties: {
    trigger?: "page" | "video";
  };
  readonly eventName = "intent_page_finished";

  constructor(props: { trigger?: "page" | "video" }) {
    this.eventProperties = {
      trigger: props.trigger ?? undefined,
    };
  }
}

/** */
export class IntentPageLoaded implements TelemetryEvent {
  readonly eventProperties: Record<string, never>;
  readonly eventName = "intent_page_loaded";

  constructor(props: Record<string, never> = {}) {
    this.eventProperties = props;
  }
}

/**
 * @param contributorBlockedEmailCount
 * @param emailCount
 * @param initiatedFrom
 * @param validEmailCount
 */
export class InviteModalClosed implements TelemetryEvent {
  readonly eventProperties: {
    contributor_blocked_email_count?: number;
    email_count?: number;
    initiated_from: "close" | "page_abandonment" | "role_change";
    valid_email_count?: number;
  };
  readonly eventName = "invite_modal_closed";

  constructor(props: {
    contributorBlockedEmailCount?: number;
    emailCount?: number;
    initiatedFrom: "close" | "page_abandonment" | "role_change";
    validEmailCount?: number;
  }) {
    this.eventProperties = {
      contributor_blocked_email_count: props.contributorBlockedEmailCount ?? undefined,
      email_count: props.emailCount ?? undefined,
      initiated_from: props.initiatedFrom,
      valid_email_count: props.validEmailCount ?? undefined,
    };
  }
}

/**
 * @param details
 * @param emailCount
 * @param role
 */
export class InviteModalEmailAdded implements TelemetryEvent {
  readonly eventProperties: {
    details?: string;
    email_count?: number;
    role?: "contributor" | "editor";
  };
  readonly eventName = "invite_modal_email_added";

  constructor(props: { details?: string; emailCount?: number; role?: "contributor" | "editor" }) {
    this.eventProperties = {
      details: props.details ?? undefined,
      email_count: props.emailCount ?? undefined,
      role: props.role ?? undefined,
    };
  }
}

/**
 * @param details
 * @param emailCount
 */
export class InviteModalEmailRemoved implements TelemetryEvent {
  readonly eventProperties: {
    details?: string;
    email_count?: number;
  };
  readonly eventName = "invite_modal_email_removed";

  constructor(props: { details?: string; emailCount?: number }) {
    this.eventProperties = {
      details: props.details ?? undefined,
      email_count: props.emailCount ?? undefined,
    };
  }
}

/**
 * @param contributorBlockedEmailCount
 * @param emailCount
 * @param formatInvalidEmailCount
 * @param memberInvalidEmailCount
 * @param recordedInvalidEmailCount
 * @param role
 * @param validEmailCount
 */
export class InviteModalEstimateReceived implements TelemetryEvent {
  readonly eventProperties: {
    contributor_blocked_email_count?: number;
    email_count: number;
    format_invalid_email_count?: number;
    member_invalid_email_count?: number;
    recorded_invalid_email_count?: number;
    role: "contributor" | "editor";
    valid_email_count?: number;
  };
  readonly eventName = "invite_modal_estimate_received";

  constructor(props: {
    contributorBlockedEmailCount?: number;
    emailCount: number;
    formatInvalidEmailCount?: number;
    memberInvalidEmailCount?: number;
    recordedInvalidEmailCount?: number;
    role: "contributor" | "editor";
    validEmailCount?: number;
  }) {
    this.eventProperties = {
      contributor_blocked_email_count: props.contributorBlockedEmailCount ?? undefined,
      email_count: props.emailCount,
      format_invalid_email_count: props.formatInvalidEmailCount ?? undefined,
      member_invalid_email_count: props.memberInvalidEmailCount ?? undefined,
      recorded_invalid_email_count: props.recordedInvalidEmailCount ?? undefined,
      role: props.role,
      valid_email_count: props.validEmailCount ?? undefined,
    };
  }
}

/**
 * @param details
 * @param emailCount
 * @param reason
 * @param role
 */
export class InviteModalInvalidEmailAdded implements TelemetryEvent {
  readonly eventProperties: {
    details?: string;
    email_count?: number;
    reason?: "invalid_format" | "already_in_team" | "ineligible_domain";
    role?: "contributor" | "editor";
  };
  readonly eventName = "invite_modal_invalid_email_added";

  constructor(props: {
    details?: string;
    emailCount?: number;
    reason?: "invalid_format" | "already_in_team" | "ineligible_domain";
    role?: "contributor" | "editor";
  }) {
    this.eventProperties = {
      details: props.details ?? undefined,
      email_count: props.emailCount ?? undefined,
      reason: props.reason ?? undefined,
      role: props.role ?? undefined,
    };
  }
}

/** @param role */
export class InviteModalShown implements TelemetryEvent {
  readonly eventProperties: {
    role: "contributor" | "editor";
  };
  readonly eventName = "invite_modal_shown";

  constructor(props: { role: "contributor" | "editor" }) {
    this.eventProperties = {
      role: props.role,
    };
  }
}

/**
 * @param contributorBlockedEmailCount
 * @param emailCount
 * @param role
 */
export class InviteModalSubmitted implements TelemetryEvent {
  readonly eventProperties: {
    contributor_blocked_email_count?: number;
    email_count?: number;
    role?: "contributor" | "editor";
  };
  readonly eventName = "invite_modal_submitted";

  constructor(props: {
    contributorBlockedEmailCount?: number;
    emailCount?: number;
    role?: "contributor" | "editor";
  }) {
    this.eventProperties = {
      contributor_blocked_email_count: props.contributorBlockedEmailCount ?? undefined,
      email_count: props.emailCount ?? undefined,
      role: props.role ?? undefined,
    };
  }
}

/**
 * @param action
 * @param details
 * @param location
 */
export class ModalClosed implements TelemetryEvent {
  readonly eventProperties: {
    action?: string;
    details?: string;
    location?: string;
  };
  readonly eventName = "modal_closed";

  constructor(props: { action?: string; details?: string; location?: string }) {
    this.eventProperties = {
      action: props.action ?? undefined,
      details: props.details ?? undefined,
      location: props.location ?? undefined,
    };
  }
}

/**
 * @param action
 * @param details
 * @param location
 */
export class ModalShown implements TelemetryEvent {
  readonly eventProperties: {
    action?: string;
    details?: string;
    location?: string;
  };
  readonly eventName = "modal_shown";

  constructor(props: { action?: string; details?: string; location?: string }) {
    this.eventProperties = {
      action: props.action ?? undefined,
      details: props.details ?? undefined,
      location: props.location ?? undefined,
    };
  }
}

/** @param lang */
export class PageTranslated implements TelemetryEvent {
  readonly eventProperties: {
    lang?: string;
  };
  readonly eventName = "page_translated";

  constructor(props: { lang?: string }) {
    this.eventProperties = {
      lang: props.lang ?? undefined,
    };
  }
}

/**
 * @param isInsideIframe
 * @param referrer
 */
export class PageVisited implements TelemetryEvent {
  readonly eventProperties: {
    is_inside_iframe?: boolean;
    referrer: string;
  };
  readonly eventName = "page_visited";

  constructor(props: { isInsideIframe?: boolean; referrer: string }) {
    this.eventProperties = {
      is_inside_iframe: props.isInsideIframe ?? undefined,
      referrer: props.referrer,
    };
  }
}

/**
 * @param paywallReason
 * @param remoteProjectId
 */
export class PaywallModalDismissed implements TelemetryEvent {
  readonly eventProperties: {
    paywall_reason: "download_shapr_file" | "project_editor_role";
    remote_project_id: string;
  };
  readonly eventName = "paywall_modal_dismissed";

  constructor(props: {
    paywallReason: "download_shapr_file" | "project_editor_role";
    remoteProjectId: string;
  }) {
    this.eventProperties = {
      paywall_reason: props.paywallReason,
      remote_project_id: props.remoteProjectId,
    };
  }
}

/**
 * @param hasSubscribeAction
 * @param paywallReason
 * @param remoteProjectId
 */
export class PaywallModalShown implements TelemetryEvent {
  readonly eventProperties: {
    has_subscribe_action: boolean;
    paywall_reason: "download_shapr_file" | "project_editor_role";
    remote_project_id: string;
  };
  readonly eventName = "paywall_modal_shown";

  constructor(props: {
    hasSubscribeAction: boolean;
    paywallReason: "download_shapr_file" | "project_editor_role";
    remoteProjectId: string;
  }) {
    this.eventProperties = {
      has_subscribe_action: props.hasSubscribeAction,
      paywall_reason: props.paywallReason,
      remote_project_id: props.remoteProjectId,
    };
  }
}

/**
 * @param paywallReason
 * @param remoteProjectId
 */
export class PaywallSubscribeClicked implements TelemetryEvent {
  readonly eventProperties: {
    paywall_reason: "download_shapr_file" | "project_editor_role";
    remote_project_id: string;
  };
  readonly eventName = "paywall_subscribe_clicked";

  constructor(props: {
    paywallReason: "download_shapr_file" | "project_editor_role";
    remoteProjectId: string;
  }) {
    this.eventProperties = {
      paywall_reason: props.paywallReason,
      remote_project_id: props.remoteProjectId,
    };
  }
}

/** @param durationMs */
export class PriceFetched implements TelemetryEvent {
  readonly eventProperties: {
    duration_ms?: number;
  };
  readonly eventName = "price_fetched";

  constructor(props: { durationMs?: number }) {
    this.eventProperties = {
      duration_ms: props.durationMs ?? undefined,
    };
  }
}

/**
 * @param currencyCode
 * @param monthlyPrice
 * @param yearlyPrice
 */
export class PriceShown implements TelemetryEvent {
  readonly eventProperties: {
    currency_code: string;
    monthly_price?: number;
    yearly_price?: number;
  };
  readonly eventName = "price_shown";

  constructor(props: { currencyCode: string; monthlyPrice?: number; yearlyPrice?: number }) {
    this.eventProperties = {
      currency_code: props.currencyCode,
      monthly_price: props.monthlyPrice ?? undefined,
      yearly_price: props.yearlyPrice ?? undefined,
    };
  }
}

/** */
export class ProgressiveProfilingCompleted implements TelemetryEvent {
  readonly eventProperties: Record<string, never>;
  readonly eventName = "progressive_profiling_completed";

  constructor(props: Record<string, never> = {}) {
    this.eventProperties = props;
  }
}

/** @param nrOfCreators */
export class ProgressiveProfilingStepCreatorsCompleted implements TelemetryEvent {
  readonly eventProperties: {
    nr_of_creators: "0" | "1-2" | "3-10" | "10+" | "Not sure";
  };
  readonly eventName = "progressive_profiling_step_creators_completed";

  constructor(props: { nrOfCreators: "0" | "1-2" | "3-10" | "10+" | "Not sure" }) {
    this.eventProperties = {
      nr_of_creators: props.nrOfCreators,
    };
  }
}

/** @param designStorage */
export class ProgressiveProfilingStepDesignStorageCompleted implements TelemetryEvent {
  readonly eventProperties: {
    design_storage: "no_method" | "general_cloud_services" | "own_servers" | "pdm_plm" | "other";
  };
  readonly eventName = "progressive_profiling_step_design_storage_completed";

  constructor(props: {
    designStorage: "no_method" | "general_cloud_services" | "own_servers" | "pdm_plm" | "other";
  }) {
    this.eventProperties = {
      design_storage: props.designStorage,
    };
  }
}

/** @param industry */
export class ProgressiveProfilingStepIndustryCompleted implements TelemetryEvent {
  readonly eventProperties: {
    industry:
      | "industrial_equipment"
      | "construction"
      | "architecture"
      | "furniture_and_interior_design"
      | "home_and_lifestyle"
      | "consumer_electronics"
      | "life_sciences_and_healthcare"
      | "transportation"
      | "media_and_entertainment"
      | "education";
  };
  readonly eventName = "progressive_profiling_step_industry_completed";

  constructor(props: {
    industry:
      | "industrial_equipment"
      | "construction"
      | "architecture"
      | "furniture_and_interior_design"
      | "home_and_lifestyle"
      | "consumer_electronics"
      | "life_sciences_and_healthcare"
      | "transportation"
      | "media_and_entertainment"
      | "education";
  }) {
    this.eventProperties = {
      industry: props.industry,
    };
  }
}

/** @param cadTool */
export class ProgressiveProfilingStepMainCadCompleted implements TelemetryEvent {
  readonly eventProperties: {
    cad_tool:
      | "alias"
      | "autocad"
      | "catia"
      | "creo"
      | "fusion_360"
      | "inventor"
      | "onshape"
      | "rhino"
      | "shapr3d"
      | "siemens_nx"
      | "sketchup"
      | "solid_edge"
      | "solidworks"
      | "none"
      | "other";
  };
  readonly eventName = "progressive_profiling_step_main_cad_completed";

  constructor(props: {
    cadTool:
      | "alias"
      | "autocad"
      | "catia"
      | "creo"
      | "fusion_360"
      | "inventor"
      | "onshape"
      | "rhino"
      | "shapr3d"
      | "siemens_nx"
      | "sketchup"
      | "solid_edge"
      | "solidworks"
      | "none"
      | "other";
  }) {
    this.eventProperties = {
      cad_tool: props.cadTool,
    };
  }
}

/** @param role */
export class ProgressiveProfilingStepRoleCompleted implements TelemetryEvent {
  readonly eventProperties: {
    role:
      | "industrial_designer"
      | "engineer_research_and_development"
      | "engineer_tool_design"
      | "team_lead"
      | "technician_machinist_operator"
      | "quality_engineer"
      | "test_engineer"
      | "product_manager"
      | "sales_purchasing"
      | "entrepreneur_owner"
      | "ceo_leadership";
  };
  readonly eventName = "progressive_profiling_step_role_completed";

  constructor(props: {
    role:
      | "industrial_designer"
      | "engineer_research_and_development"
      | "engineer_tool_design"
      | "team_lead"
      | "technician_machinist_operator"
      | "quality_engineer"
      | "test_engineer"
      | "product_manager"
      | "sales_purchasing"
      | "entrepreneur_owner"
      | "ceo_leadership";
  }) {
    this.eventProperties = {
      role: props.role,
    };
  }
}

/** @param nrOfViewers */
export class ProgressiveProfilingStepViewersCompleted implements TelemetryEvent {
  readonly eventProperties: {
    nr_of_viewers: "0" | "1-2" | "3-10" | "10+" | "Not sure";
  };
  readonly eventName = "progressive_profiling_step_viewers_completed";

  constructor(props: { nrOfViewers: "0" | "1-2" | "3-10" | "10+" | "Not sure" }) {
    this.eventProperties = {
      nr_of_viewers: props.nrOfViewers,
    };
  }
}

/**
 * @param projectId
 * @param projectTeamId
 * @param role
 */
export class ProjectPageAccessRequestAccepted implements TelemetryEvent {
  readonly eventProperties: {
    project_id: string;
    project_team_id: string;
    role: string;
  };
  readonly eventName = "project_page_access_request_accepted";

  constructor(props: { projectId: string; projectTeamId: string; role: string }) {
    this.eventProperties = {
      project_id: props.projectId,
      project_team_id: props.projectTeamId,
      role: props.role,
    };
  }
}

/**
 * @param projectId
 * @param projectTeamId
 */
export class ProjectPageAccessRequestRejected implements TelemetryEvent {
  readonly eventProperties: {
    project_id: string;
    project_team_id: string;
  };
  readonly eventName = "project_page_access_request_rejected";

  constructor(props: { projectId: string; projectTeamId: string }) {
    this.eventProperties = {
      project_id: props.projectId,
      project_team_id: props.projectTeamId,
    };
  }
}

/** @param projectId */
export class ProjectPageAccessRequested implements TelemetryEvent {
  readonly eventProperties: {
    project_id: string;
  };
  readonly eventName = "project_page_access_requested";

  constructor(props: { projectId: string }) {
    this.eventProperties = {
      project_id: props.projectId,
    };
  }
}

/** @param projectId */
export class ProjectPageAccessSwitchUser implements TelemetryEvent {
  readonly eventProperties: {
    project_id: string;
  };
  readonly eventName = "project_page_access_switch_user";

  constructor(props: { projectId: string }) {
    this.eventProperties = {
      project_id: props.projectId,
    };
  }
}

/**
 * @param projectId
 * @param projectTeamId
 */
export class ProjectPageDownloadFileClicked implements TelemetryEvent {
  readonly eventProperties: {
    project_id: string;
    project_team_id: string;
  };
  readonly eventName = "project_page_download_file_clicked";

  constructor(props: { projectId: string; projectTeamId: string }) {
    this.eventProperties = {
      project_id: props.projectId,
      project_team_id: props.projectTeamId,
    };
  }
}

/**
 * @param projectId
 * @param projectTeamId
 */
export class ProjectPageDownloadFileShown implements TelemetryEvent {
  readonly eventProperties: {
    project_id: string;
    project_team_id: string;
  };
  readonly eventName = "project_page_download_file_shown";

  constructor(props: { projectId: string; projectTeamId: string }) {
    this.eventProperties = {
      project_id: props.projectId,
      project_team_id: props.projectTeamId,
    };
  }
}

/**
 * @param featureTourType
 * @param projectId
 */
export class ProjectPageFeatureTourClose implements TelemetryEvent {
  readonly eventProperties: {
    feature_tour_type: string;
    project_id: string;
  };
  readonly eventName = "project_page_feature_tour_close";

  constructor(props: { featureTourType: string; projectId: string }) {
    this.eventProperties = {
      feature_tour_type: props.featureTourType,
      project_id: props.projectId,
    };
  }
}

/**
 * @param featureTourType
 * @param projectId
 */
export class ProjectPageFeatureTourCompleted implements TelemetryEvent {
  readonly eventProperties: {
    feature_tour_type: string;
    project_id: string;
  };
  readonly eventName = "project_page_feature_tour_completed";

  constructor(props: { featureTourType: string; projectId: string }) {
    this.eventProperties = {
      feature_tour_type: props.featureTourType,
      project_id: props.projectId,
    };
  }
}

/**
 * @param featureTourStep
 * @param featureTourType
 * @param projectId
 */
export class ProjectPageFeatureTourStepShown implements TelemetryEvent {
  readonly eventProperties: {
    feature_tour_step: number;
    feature_tour_type: string;
    project_id: string;
  };
  readonly eventName = "project_page_feature_tour_step_shown";

  constructor(props: { featureTourStep: number; featureTourType: string; projectId: string }) {
    this.eventProperties = {
      feature_tour_step: props.featureTourStep,
      feature_tour_type: props.featureTourType,
      project_id: props.projectId,
    };
  }
}

/**
 * @param accessStatus
 * @param hasReviewLink
 * @param notFound
 * @param projectId
 * @param projectTeamId
 */
export class ProjectPageLoaded implements TelemetryEvent {
  readonly eventProperties: {
    access_status?: "success" | "not_found" | "forbidden" | "pending_access" | "unverified_email";
    has_review_link?: boolean;
    not_found?: boolean;
    project_id: string;
    project_team_id?: string;
  };
  readonly eventName = "project_page_loaded";

  constructor(props: {
    accessStatus?: "success" | "not_found" | "forbidden" | "pending_access" | "unverified_email";
    hasReviewLink?: boolean;
    notFound?: boolean;
    projectId: string;
    projectTeamId?: string;
  }) {
    this.eventProperties = {
      access_status: props.accessStatus ?? undefined,
      has_review_link: props.hasReviewLink ?? undefined,
      not_found: props.notFound ?? undefined,
      project_id: props.projectId,
      project_team_id: props.projectTeamId ?? undefined,
    };
  }
}

/** @param newState */
export class ProjectSidebarToggled implements TelemetryEvent {
  readonly eventProperties: {
    new_state: "open" | "close";
  };
  readonly eventName = "project_sidebar_toggled";

  constructor(props: { newState: "open" | "close" }) {
    this.eventProperties = {
      new_state: props.newState,
    };
  }
}

/**
 * @param configurationType
 * @param duration
 * @param startTime
 */
export class RemoteConfigReceived implements TelemetryEvent {
  readonly eventProperties: {
    configuration_type?: "user" | "tracking";
    duration: number;
    start_time?: number;
  };
  readonly eventName = "remote_config_received";

  constructor(props: {
    configurationType?: "user" | "tracking";
    duration: number;
    startTime?: number;
  }) {
    this.eventProperties = {
      configuration_type: props.configurationType ?? undefined,
      duration: props.duration,
      start_time: props.startTime ?? undefined,
    };
  }
}

/** */
export class RenewalTooltipShown implements TelemetryEvent {
  readonly eventProperties: Record<string, never>;
  readonly eventName = "renewal_tooltip_shown";

  constructor(props: Record<string, never> = {}) {
    this.eventProperties = props;
  }
}

/** @param location */
export class RequestVerificationEmailClicked implements TelemetryEvent {
  readonly eventProperties: {
    location?: "top_menu" | "request_access" | "checkout" | "2fa_enable";
  };
  readonly eventName = "request_verification_email_clicked";

  constructor(props: { location?: "top_menu" | "request_access" | "checkout" | "2fa_enable" }) {
    this.eventProperties = {
      location: props.location ?? undefined,
    };
  }
}

/** */
export class SegmentationSurveyCompletedV3 implements TelemetryEvent {
  readonly eventProperties: Record<string, never>;
  readonly eventName = "segmentation_survey_completed_v3";

  constructor(props: Record<string, never> = {}) {
    this.eventProperties = props;
  }
}

/**
 * @param cadTools
 * @param details
 */
export class SegmentationSurveyStepCadCompleted implements TelemetryEvent {
  readonly eventProperties: {
    cad_tools: (
      | "none"
      | "alias"
      | "autocad"
      | "catia"
      | "creo"
      | "fusion_360"
      | "inventor"
      | "onshape"
      | "shapr3d"
      | "sketchup"
      | "siemens_nx"
      | "solid_edge"
      | "solidworks"
      | "rhino"
      | "other"
    )[];
    details?: string;
  };
  readonly eventName = "segmentation_survey_step_cad_completed";

  constructor(props: {
    cadTools: (
      | "none"
      | "alias"
      | "autocad"
      | "catia"
      | "creo"
      | "fusion_360"
      | "inventor"
      | "onshape"
      | "shapr3d"
      | "sketchup"
      | "siemens_nx"
      | "solid_edge"
      | "solidworks"
      | "rhino"
      | "other"
    )[];
    details?: string;
  }) {
    this.eventProperties = {
      cad_tools: props.cadTools,
      details: props.details ?? undefined,
    };
  }
}

/**
 * @param details
 * @param features
 */
export class SegmentationSurveyStepFeaturesCompleted implements TelemetryEvent {
  readonly eventProperties: {
    details?: string;
    features: (
      | "communication"
      | "iterating"
      | "mobility"
      | "quick_onboarding"
      | "rendering"
      | "ar"
      | "other"
      | "quick_onboarding"
    )[];
  };
  readonly eventName = "segmentation_survey_step_features_completed";

  constructor(props: {
    details?: string;
    features: (
      | "communication"
      | "iterating"
      | "mobility"
      | "quick_onboarding"
      | "rendering"
      | "ar"
      | "other"
      | "quick_onboarding"
    )[];
  }) {
    this.eventProperties = {
      details: props.details ?? undefined,
      features: props.features,
    };
  }
}

/**
 * @param details
 * @param industry
 */
export class SegmentationSurveyStepIndustryCompleted implements TelemetryEvent {
  readonly eventProperties: {
    details?: string;
    industry:
      | "transportation"
      | "industrial_equipment"
      | "high_tech"
      | "home_and_lifestyle"
      | "fmcg"
      | "construction_products"
      | "other";
  };
  readonly eventName = "segmentation_survey_step_industry_completed";

  constructor(props: {
    details?: string;
    industry:
      | "transportation"
      | "industrial_equipment"
      | "high_tech"
      | "home_and_lifestyle"
      | "fmcg"
      | "construction_products"
      | "other";
  }) {
    this.eventProperties = {
      details: props.details ?? undefined,
      industry: props.industry,
    };
  }
}

/** @param orgSize */
export class SegmentationSurveyStepOrgSizeCompleted implements TelemetryEvent {
  readonly eventProperties: {
    org_size: "me" | "2-50" | "51-250" | "251-1000" | "1001-10000" | "10000+";
  };
  readonly eventName = "segmentation_survey_step_org_size_completed";

  constructor(props: { orgSize: "me" | "2-50" | "51-250" | "251-1000" | "1001-10000" | "10000+" }) {
    this.eventProperties = {
      org_size: props.orgSize,
    };
  }
}

/**
 * @param details
 * @param userJobRole
 */
export class SegmentationSurveyStepRoleCompleted implements TelemetryEvent {
  readonly eventProperties: {
    details?: string;
    user_job_role: (
      | "concepting"
      | "communication"
      | "prototyping"
      | "engineering"
      | "manufacturing"
      | "review"
      | "other"
    )[];
  };
  readonly eventName = "segmentation_survey_step_role_completed";

  constructor(props: {
    details?: string;
    userJobRole: (
      | "concepting"
      | "communication"
      | "prototyping"
      | "engineering"
      | "manufacturing"
      | "review"
      | "other"
    )[];
  }) {
    this.eventProperties = {
      details: props.details ?? undefined,
      user_job_role: props.userJobRole,
    };
  }
}

/**
 * @param details
 * @param usageIntent
 */
export class SegmentationSurveyStepUsageIntentCompleted implements TelemetryEvent {
  readonly eventProperties: {
    details?: string;
    usage_intent: "professional" | "personal" | "edu" | "other";
  };
  readonly eventName = "segmentation_survey_step_usage_intent_completed";

  constructor(props: {
    details?: string;
    usageIntent: "professional" | "personal" | "edu" | "other";
  }) {
    this.eventProperties = {
      details: props.details ?? undefined,
      usage_intent: props.usageIntent,
    };
  }
}

/**
 * @param details
 * @param work
 */
export class SegmentationSurveyStepWorkCompleted implements TelemetryEvent {
  readonly eventProperties: {
    details?: string;
    work:
      | "product_manufacturing"
      | "architecture"
      | "digital_entertainment"
      | "student_or_educator"
      | "other";
  };
  readonly eventName = "segmentation_survey_step_work_completed";

  constructor(props: {
    details?: string;
    work:
      | "product_manufacturing"
      | "architecture"
      | "digital_entertainment"
      | "student_or_educator"
      | "other";
  }) {
    this.eventProperties = {
      details: props.details ?? undefined,
      work: props.work,
    };
  }
}

/** @param duration */
export class SessionPaused implements TelemetryEvent {
  readonly eventProperties: {
    duration?: number;
  };
  readonly eventName = "session_paused";

  constructor(props: { duration?: number }) {
    this.eventProperties = {
      duration: props.duration ?? undefined,
    };
  }
}

/**
 * @param paymentPlatform
 * @param subscriptionPeriodChosen
 */
export class SubscriptionPeriodSwitched implements TelemetryEvent {
  readonly eventProperties: {
    payment_platform: "chargebee" | "paddle";
    subscription_period_chosen: "monthly" | "yearly";
  };
  readonly eventName = "subscription_period_switched";

  constructor(props: {
    paymentPlatform: "chargebee" | "paddle";
    subscriptionPeriodChosen: "monthly" | "yearly";
  }) {
    this.eventProperties = {
      payment_platform: props.paymentPlatform,
      subscription_period_chosen: props.subscriptionPeriodChosen,
    };
  }
}

/** @param action */
export class TwoFactorDisableModalAct implements TelemetryEvent {
  readonly eventProperties: {
    action?: "keep_enabled" | "disable_2fa";
  };
  readonly eventName = "two_factor_disable_modal_act";

  constructor(props: { action?: "keep_enabled" | "disable_2fa" }) {
    this.eventProperties = {
      action: props.action ?? undefined,
    };
  }
}

/** @param paymentType */
export class UpdatePaymentSourceSuccess implements TelemetryEvent {
  readonly eventProperties: {
    payment_type: "apple_pay" | "card";
  };
  readonly eventName = "update_payment_source_success";

  constructor(props: { paymentType: "apple_pay" | "card" }) {
    this.eventProperties = {
      payment_type: props.paymentType,
    };
  }
}

/** */
export class UrlChanged implements TelemetryEvent {
  readonly eventProperties: Record<string, never>;
  readonly eventName = "url_changed";

  constructor(props: Record<string, never> = {}) {
    this.eventProperties = props;
  }
}

/** @param appTrackingId */
export class UserLoggedIn implements TelemetryEvent {
  readonly eventProperties: {
    app_tracking_id?: string;
  };
  readonly eventName = "user_logged_in";

  constructor(props: { appTrackingId?: string }) {
    this.eventProperties = {
      app_tracking_id: props.appTrackingId ?? undefined,
    };
  }
}

/**
 * @param appTrackingId
 * @param marketingCookies
 * @param type
 */
export class UserSignedUp implements TelemetryEvent {
  readonly eventProperties: {
    app_tracking_id?: string;
    marketing_cookies?: {
      _fbc?: string;
      _fbp?: string;
      _ga?: string;
      _gclid?: string;
      _msclkid?: string;
    };
    type?: "email";
  };
  readonly eventName = "user_signed_up";

  constructor(props: {
    appTrackingId?: string;
    marketingCookies?: {
      _fbc?: string;
      _fbp?: string;
      _ga?: string;
      _gclid?: string;
      _msclkid?: string;
    };
    type?: "email";
  }) {
    this.eventProperties = {
      app_tracking_id: props.appTrackingId ?? undefined,
      marketing_cookies: props.marketingCookies ?? undefined,
      type: props.type ?? undefined,
    };
  }
}

/**
 * @param location
 * @param state
 */
export class VersionSelectorVisibilityChanged implements TelemetryEvent {
  readonly eventProperties: {
    location: "project_sidebar";
    state: "visible" | "hidden";
  };
  readonly eventName = "version_selector_visibility_changed";

  constructor(props: { location: "project_sidebar"; state: "visible" | "hidden" }) {
    this.eventProperties = {
      location: props.location,
      state: props.state,
    };
  }
}

/**
 * @param from
 * @param videoId
 */
export class VideoStarted implements TelemetryEvent {
  readonly eventProperties: {
    from: "intent_page" | "clicked_previous" | "clicked_next" | "auto_play";
    video_id:
      | "model_parts"
      | "model_anywhere"
      | "communicate_design_intent"
      | "review_edit_ar"
      | "generative_render"
      | "fix_cad_import";
  };
  readonly eventName = "video_started";

  constructor(props: {
    from: "intent_page" | "clicked_previous" | "clicked_next" | "auto_play";
    videoId:
      | "model_parts"
      | "model_anywhere"
      | "communicate_design_intent"
      | "review_edit_ar"
      | "generative_render"
      | "fix_cad_import";
  }) {
    this.eventProperties = {
      from: props.from,
      video_id: props.videoId,
    };
  }
}

/**
 * @param reason
 * @param videoId
 */
export class VideoStopped implements TelemetryEvent {
  readonly eventProperties: {
    reason: "closed" | "start_modeling_clicked" | "previous_clicked" | "next_clicked" | "finished";
    video_id:
      | "model_parts"
      | "model_anywhere"
      | "communicate_design_intent"
      | "review_edit_ar"
      | "generative_render"
      | "fix_cad_import";
  };
  readonly eventName = "video_stopped";

  constructor(props: {
    reason: "closed" | "start_modeling_clicked" | "previous_clicked" | "next_clicked" | "finished";
    videoId:
      | "model_parts"
      | "model_anywhere"
      | "communicate_design_intent"
      | "review_edit_ar"
      | "generative_render"
      | "fix_cad_import";
  }) {
    this.eventProperties = {
      reason: props.reason,
      video_id: props.videoId,
    };
  }
}

/** @param activeTeamId */
export class WebDashboardTeamSwitcherClicked implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id: string;
  };
  readonly eventName = "web_dashboard_team_switcher_clicked";

  constructor(props: { activeTeamId: string }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId,
    };
  }
}

/**
 * @param activeTeamId
 * @param selectedTeamId
 */
export class WebDashboardTeamSwitcherItemClicked implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id: string;
    selected_team_id: string;
  };
  readonly eventName = "web_dashboard_team_switcher_item_clicked";

  constructor(props: { activeTeamId: string; selectedTeamId: string }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId,
      selected_team_id: props.selectedTeamId,
    };
  }
}

/**
 * @param activeTeamId
 * @param location
 * @param viewLinkId
 */
export class WebDashboardViewLinkClicked implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    location?: "dashboard" | "project_page";
    view_link_id: string;
  };
  readonly eventName = "web_dashboard_view_link_clicked";

  constructor(props: {
    activeTeamId?: string;
    location?: "dashboard" | "project_page";
    viewLinkId: string;
  }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location ?? undefined,
      view_link_id: props.viewLinkId,
    };
  }
}

/**
 * @param action
 * @param activeTeamId
 * @param location
 * @param viewLinkId
 */
export class WebDashboardViewLinkContextMenuActed implements TelemetryEvent {
  readonly eventProperties: {
    action: "copy_link" | "open_link" | "open_link_in_new_tab" | "unpublish";
    active_team_id?: string;
    location?: "dashboard" | "project_page";
    view_link_id: string;
  };
  readonly eventName = "web_dashboard_view_link_context_menu_acted";

  constructor(props: {
    action: "copy_link" | "open_link" | "open_link_in_new_tab" | "unpublish";
    activeTeamId?: string;
    location?: "dashboard" | "project_page";
    viewLinkId: string;
  }) {
    this.eventProperties = {
      action: props.action,
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location ?? undefined,
      view_link_id: props.viewLinkId,
    };
  }
}

/**
 * @param activeTeamId
 * @param location
 * @param viewLinkId
 */
export class WebDashboardViewLinkContextMenuOpened implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    location?: "dashboard" | "project_page";
    view_link_id: string;
  };
  readonly eventName = "web_dashboard_view_link_context_menu_opened";

  constructor(props: {
    activeTeamId?: string;
    location?: "dashboard" | "project_page";
    viewLinkId: string;
  }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location ?? undefined,
      view_link_id: props.viewLinkId,
    };
  }
}

/**
 * @param activeTeamId
 * @param location
 * @param viewLinkId
 */
export class WebDashboardViewLinkCopied implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    location?: "dashboard" | "project_page";
    view_link_id: string;
  };
  readonly eventName = "web_dashboard_view_link_copied";

  constructor(props: {
    activeTeamId?: string;
    location?: "dashboard" | "project_page";
    viewLinkId: string;
  }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location ?? undefined,
      view_link_id: props.viewLinkId,
    };
  }
}

/**
 * @param activeTeamId
 * @param location
 * @param viewLinkId
 */
export class WebDashboardViewLinkUnpublishConfirmed implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    location?: "dashboard" | "project_page";
    view_link_id: string;
  };
  readonly eventName = "web_dashboard_view_link_unpublish_confirmed";

  constructor(props: {
    activeTeamId?: string;
    location?: "dashboard" | "project_page";
    viewLinkId: string;
  }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location ?? undefined,
      view_link_id: props.viewLinkId,
    };
  }
}

/**
 * @param activeTeamId
 * @param location
 * @param projectId
 * @param type
 * @param viewLinkId
 */
export class WebLinkClicked implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    location?: "review_links" | "shared_with_me" | "project_page";
    project_id?: string;
    type: "view_link" | "project_page_link";
    view_link_id?: string;
  };
  readonly eventName = "web_link_clicked";

  constructor(props: {
    activeTeamId?: string;
    location?: "review_links" | "shared_with_me" | "project_page";
    projectId?: string;
    type: "view_link" | "project_page_link";
    viewLinkId?: string;
  }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location ?? undefined,
      project_id: props.projectId ?? undefined,
      type: props.type,
      view_link_id: props.viewLinkId ?? undefined,
    };
  }
}

/**
 * @param action
 * @param activeTeamId
 * @param location
 * @param projectId
 * @param type
 * @param viewLinkId
 */
export class WebLinkContextMenuActed implements TelemetryEvent {
  readonly eventProperties: {
    action: "copy_link" | "open_link" | "open_link_in_new_tab" | "unpublish";
    active_team_id?: string;
    location?: "review_links" | "shared_with_me" | "project_page";
    project_id?: string;
    type: "view_link" | "project_page_link";
    view_link_id?: string;
  };
  readonly eventName = "web_link_context_menu_acted";

  constructor(props: {
    action: "copy_link" | "open_link" | "open_link_in_new_tab" | "unpublish";
    activeTeamId?: string;
    location?: "review_links" | "shared_with_me" | "project_page";
    projectId?: string;
    type: "view_link" | "project_page_link";
    viewLinkId?: string;
  }) {
    this.eventProperties = {
      action: props.action,
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location ?? undefined,
      project_id: props.projectId ?? undefined,
      type: props.type,
      view_link_id: props.viewLinkId ?? undefined,
    };
  }
}

/**
 * @param activeTeamId
 * @param location
 * @param projectId
 * @param type
 * @param viewLinkId
 */
export class WebLinkContextMenuOpened implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    location?: "review_links" | "shared_with_me" | "project_page";
    project_id?: string;
    type: "view_link" | "project_page_link";
    view_link_id?: string;
  };
  readonly eventName = "web_link_context_menu_opened";

  constructor(props: {
    activeTeamId?: string;
    location?: "review_links" | "shared_with_me" | "project_page";
    projectId?: string;
    type: "view_link" | "project_page_link";
    viewLinkId?: string;
  }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location ?? undefined,
      project_id: props.projectId ?? undefined,
      type: props.type,
      view_link_id: props.viewLinkId ?? undefined,
    };
  }
}

/**
 * @param activeTeamId
 * @param location
 * @param projectId
 * @param type
 * @param viewLinkId
 */
export class WebLinkCopied implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    location?: "review_links" | "shared_with_me" | "project_page";
    project_id?: string;
    type: "view_link" | "project_page_link";
    view_link_id?: string;
  };
  readonly eventName = "web_link_copied";

  constructor(props: {
    activeTeamId?: string;
    location?: "review_links" | "shared_with_me" | "project_page";
    projectId?: string;
    type: "view_link" | "project_page_link";
    viewLinkId?: string;
  }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location ?? undefined,
      project_id: props.projectId ?? undefined,
      type: props.type,
      view_link_id: props.viewLinkId ?? undefined,
    };
  }
}

/**
 * @param activeTeamId
 * @param location
 * @param type
 * @param viewLinkId
 */
export class WebLinkUnpublishConfirmed implements TelemetryEvent {
  readonly eventProperties: {
    active_team_id?: string;
    location?: "review_links" | "shared_with_me" | "project_page";
    type: "view_link";
    view_link_id?: string;
  };
  readonly eventName = "web_link_unpublish_confirmed";

  constructor(props: {
    activeTeamId?: string;
    location?: "review_links" | "shared_with_me" | "project_page";
    type: "view_link";
    viewLinkId?: string;
  }) {
    this.eventProperties = {
      active_team_id: props.activeTeamId ?? undefined,
      location: props.location ?? undefined,
      type: props.type,
      view_link_id: props.viewLinkId ?? undefined,
    };
  }
}

/**
 * @param projectUserRole
 * @param remoteProjectId
 * @param sharedResource
 * @param spaceId
 * @param teamUserRole
 * @param viewLinkId
 * @param webExportId
 */
export class WebShareModalEmailAdded implements TelemetryEvent {
  readonly eventProperties: {
    project_user_role?: string;
    remote_project_id?: string;
    shared_resource?: "project" | "review_link";
    space_id?: string;
    team_user_role?: string;
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_share_modal_email_added";

  constructor(props: {
    projectUserRole?: string;
    remoteProjectId?: string;
    sharedResource?: "project" | "review_link";
    spaceId?: string;
    teamUserRole?: string;
    viewLinkId?: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      project_user_role: props.projectUserRole ?? undefined,
      remote_project_id: props.remoteProjectId ?? undefined,
      shared_resource: props.sharedResource ?? undefined,
      space_id: props.spaceId ?? undefined,
      team_user_role: props.teamUserRole ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param projectUserRole
 * @param remoteProjectId
 * @param sharedResource
 * @param spaceId
 * @param teamUserRole
 * @param viewLinkId
 * @param webExportId
 */
export class WebShareModalEmailRemoved implements TelemetryEvent {
  readonly eventProperties: {
    project_user_role?: string;
    remote_project_id?: string;
    shared_resource?: "project" | "review_link";
    space_id?: string;
    team_user_role?: string;
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_share_modal_email_removed";

  constructor(props: {
    projectUserRole?: string;
    remoteProjectId?: string;
    sharedResource?: "project" | "review_link";
    spaceId?: string;
    teamUserRole?: string;
    viewLinkId?: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      project_user_role: props.projectUserRole ?? undefined,
      remote_project_id: props.remoteProjectId ?? undefined,
      shared_resource: props.sharedResource ?? undefined,
      space_id: props.spaceId ?? undefined,
      team_user_role: props.teamUserRole ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param projectUserRole
 * @param remoteProjectId
 * @param sharedResource
 * @param spaceId
 * @param teamUserRole
 * @param viewLinkId
 * @param webExportId
 */
export class WebShareModalInvalidEmailAdded implements TelemetryEvent {
  readonly eventProperties: {
    project_user_role?: string;
    remote_project_id?: string;
    shared_resource?: "project" | "review_link";
    space_id?: string;
    team_user_role?: string;
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_share_modal_invalid_email_added";

  constructor(props: {
    projectUserRole?: string;
    remoteProjectId?: string;
    sharedResource?: "project" | "review_link";
    spaceId?: string;
    teamUserRole?: string;
    viewLinkId?: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      project_user_role: props.projectUserRole ?? undefined,
      remote_project_id: props.remoteProjectId ?? undefined,
      shared_resource: props.sharedResource ?? undefined,
      space_id: props.spaceId ?? undefined,
      team_user_role: props.teamUserRole ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param projectUserRole
 * @param remoteProjectId
 * @param role
 * @param sharedResource
 * @param spaceId
 * @param teamUserRole
 * @param viewLinkId
 * @param webExportId
 */
export class WebShareModalInvitePressed implements TelemetryEvent {
  readonly eventProperties: {
    project_user_role?: string;
    remote_project_id?: string;
    role?: string;
    shared_resource?: "project" | "review_link";
    space_id?: string;
    team_user_role?: string;
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_share_modal_invite_pressed";

  constructor(props: {
    projectUserRole?: string;
    remoteProjectId?: string;
    role?: string;
    sharedResource?: "project" | "review_link";
    spaceId?: string;
    teamUserRole?: string;
    viewLinkId?: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      project_user_role: props.projectUserRole ?? undefined,
      remote_project_id: props.remoteProjectId ?? undefined,
      role: props.role ?? undefined,
      shared_resource: props.sharedResource ?? undefined,
      space_id: props.spaceId ?? undefined,
      team_user_role: props.teamUserRole ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param newValue
 * @param oldValue
 * @param projectUserRole
 * @param remoteProjectId
 * @param sharedResource
 * @param spaceId
 * @param teamUserRole
 * @param viewLinkId
 * @param webExportId
 */
export class WebShareModalInviteRoleChanged implements TelemetryEvent {
  readonly eventProperties: {
    new_value: string;
    old_value: string;
    project_user_role?: string;
    remote_project_id?: string;
    shared_resource?: "project" | "review_link";
    space_id?: string;
    team_user_role?: string;
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_share_modal_invite_role_changed";

  constructor(props: {
    newValue: string;
    oldValue: string;
    projectUserRole?: string;
    remoteProjectId?: string;
    sharedResource?: "project" | "review_link";
    spaceId?: string;
    teamUserRole?: string;
    viewLinkId?: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      new_value: props.newValue,
      old_value: props.oldValue,
      project_user_role: props.projectUserRole ?? undefined,
      remote_project_id: props.remoteProjectId ?? undefined,
      shared_resource: props.sharedResource ?? undefined,
      space_id: props.spaceId ?? undefined,
      team_user_role: props.teamUserRole ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param affectedUserId
 * @param invitationId
 * @param projectUserRole
 * @param remoteProjectId
 * @param sharedResource
 * @param spaceId
 * @param teamUserRole
 * @param viewLinkId
 * @param webExportId
 */
export class WebShareModalMemberRemoveConfirmed implements TelemetryEvent {
  readonly eventProperties: {
    affected_user_id?: string;
    invitation_id?: string;
    project_user_role?: string;
    remote_project_id?: string;
    shared_resource?: "project" | "review_link";
    space_id?: string;
    team_user_role?: string;
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_share_modal_member_remove_confirmed";

  constructor(props: {
    affectedUserId?: string;
    invitationId?: string;
    projectUserRole?: string;
    remoteProjectId?: string;
    sharedResource?: "project" | "review_link";
    spaceId?: string;
    teamUserRole?: string;
    viewLinkId?: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      affected_user_id: props.affectedUserId ?? undefined,
      invitation_id: props.invitationId ?? undefined,
      project_user_role: props.projectUserRole ?? undefined,
      remote_project_id: props.remoteProjectId ?? undefined,
      shared_resource: props.sharedResource ?? undefined,
      space_id: props.spaceId ?? undefined,
      team_user_role: props.teamUserRole ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param affectedUserId
 * @param invitationId
 * @param projectUserRole
 * @param remoteProjectId
 * @param sharedResource
 * @param spaceId
 * @param teamUserRole
 * @param viewLinkId
 * @param webExportId
 */
export class WebShareModalMemberRemoveIntended implements TelemetryEvent {
  readonly eventProperties: {
    affected_user_id?: string;
    invitation_id?: string;
    project_user_role?: string;
    remote_project_id?: string;
    shared_resource?: "project" | "review_link";
    space_id?: string;
    team_user_role?: string;
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_share_modal_member_remove_intended";

  constructor(props: {
    affectedUserId?: string;
    invitationId?: string;
    projectUserRole?: string;
    remoteProjectId?: string;
    sharedResource?: "project" | "review_link";
    spaceId?: string;
    teamUserRole?: string;
    viewLinkId?: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      affected_user_id: props.affectedUserId ?? undefined,
      invitation_id: props.invitationId ?? undefined,
      project_user_role: props.projectUserRole ?? undefined,
      remote_project_id: props.remoteProjectId ?? undefined,
      shared_resource: props.sharedResource ?? undefined,
      space_id: props.spaceId ?? undefined,
      team_user_role: props.teamUserRole ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param affectedUserId
 * @param invitationId
 * @param newValue
 * @param oldValue
 * @param projectUserRole
 * @param remoteProjectId
 * @param sharedResource
 * @param spaceId
 * @param teamUserRole
 * @param viewLinkId
 * @param webExportId
 */
export class WebShareModalMemberRoleChanged implements TelemetryEvent {
  readonly eventProperties: {
    affected_user_id?: string;
    invitation_id?: string;
    new_value: string;
    old_value: string;
    project_user_role?: string;
    remote_project_id?: string;
    shared_resource?: "project" | "review_link";
    space_id?: string;
    team_user_role?: string;
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_share_modal_member_role_changed";

  constructor(props: {
    affectedUserId?: string;
    invitationId?: string;
    newValue: string;
    oldValue: string;
    projectUserRole?: string;
    remoteProjectId?: string;
    sharedResource?: "project" | "review_link";
    spaceId?: string;
    teamUserRole?: string;
    viewLinkId?: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      affected_user_id: props.affectedUserId ?? undefined,
      invitation_id: props.invitationId ?? undefined,
      new_value: props.newValue,
      old_value: props.oldValue,
      project_user_role: props.projectUserRole ?? undefined,
      remote_project_id: props.remoteProjectId ?? undefined,
      shared_resource: props.sharedResource ?? undefined,
      space_id: props.spaceId ?? undefined,
      team_user_role: props.teamUserRole ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param projectUserRole
 * @param remoteProjectId
 * @param sharedResource
 * @param spaceId
 * @param teamUserRole
 * @param viewLinkId
 * @param visible
 * @param webExportId
 */
export class WebShareModalVisibilityChanged implements TelemetryEvent {
  readonly eventProperties: {
    project_user_role?: string;
    remote_project_id?: string;
    shared_resource?: "project" | "review_link";
    space_id?: string;
    team_user_role?: string;
    view_link_id?: string;
    visible: boolean;
    web_export_id?: string;
  };
  readonly eventName = "web_share_modal_visibility_changed";

  constructor(props: {
    projectUserRole?: string;
    remoteProjectId?: string;
    sharedResource?: "project" | "review_link";
    spaceId?: string;
    teamUserRole?: string;
    viewLinkId?: string;
    visible: boolean;
    webExportId?: string;
  }) {
    this.eventProperties = {
      project_user_role: props.projectUserRole ?? undefined,
      remote_project_id: props.remoteProjectId ?? undefined,
      shared_resource: props.sharedResource ?? undefined,
      space_id: props.spaceId ?? undefined,
      team_user_role: props.teamUserRole ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
      visible: props.visible,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param initiatedFrom
 * @param newTool
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerActiveToolChanged implements TelemetryEvent {
  readonly eventProperties: {
    initiated_from: "tool_menu" | "keyboard_shortcut" | "mouse_click";
    new_tool: "camera_control" | "commenting";
    view_link_id: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_active_tool_changed";

  constructor(props: {
    initiatedFrom: "tool_menu" | "keyboard_shortcut" | "mouse_click";
    newTool: "camera_control" | "commenting";
    viewLinkId: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      initiated_from: props.initiatedFrom,
      new_tool: props.newTool,
      view_link_id: props.viewLinkId,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param type
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerAnnotationPlaced implements TelemetryEvent {
  readonly eventProperties: {
    type: "model";
    view_link_id: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_annotation_placed";

  constructor(props: { type: "model"; viewLinkId: string; webExportId?: string }) {
    this.eventProperties = {
      type: props.type,
      view_link_id: props.viewLinkId,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param commentId
 * @param type
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerCommentAdded implements TelemetryEvent {
  readonly eventProperties: {
    comment_id: string;
    type: "model";
    view_link_id: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_comment_added";

  constructor(props: {
    commentId: string;
    type: "model";
    viewLinkId: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      comment_id: props.commentId,
      type: props.type,
      view_link_id: props.viewLinkId,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param commentId
 * @param viewLinkId
 * @param visible
 * @param webExportId
 */
export class WebViewerCommentContextMenuVisibilityChanged implements TelemetryEvent {
  readonly eventProperties: {
    comment_id: string;
    view_link_id: string;
    visible: boolean;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_comment_context_menu_visibility_changed";

  constructor(props: {
    commentId: string;
    viewLinkId: string;
    visible: boolean;
    webExportId?: string;
  }) {
    this.eventProperties = {
      comment_id: props.commentId,
      view_link_id: props.viewLinkId,
      visible: props.visible,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param commentId
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerCommentDeleteConfirmed implements TelemetryEvent {
  readonly eventProperties: {
    comment_id: string;
    view_link_id: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_comment_delete_confirmed";

  constructor(props: { commentId: string; viewLinkId: string; webExportId?: string }) {
    this.eventProperties = {
      comment_id: props.commentId,
      view_link_id: props.viewLinkId,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param commentId
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerCommentDeleteIntended implements TelemetryEvent {
  readonly eventProperties: {
    comment_id: string;
    view_link_id: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_comment_delete_intended";

  constructor(props: { commentId: string; viewLinkId: string; webExportId?: string }) {
    this.eventProperties = {
      comment_id: props.commentId,
      view_link_id: props.viewLinkId,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param commentId
 * @param source
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerCommentSelected implements TelemetryEvent {
  readonly eventProperties: {
    comment_id: string;
    source: "canvas" | "sidebar";
    view_link_id: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_comment_selected";

  constructor(props: {
    commentId: string;
    source: "canvas" | "sidebar";
    viewLinkId: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      comment_id: props.commentId,
      source: props.source,
      view_link_id: props.viewLinkId,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerCommentingDisplayed implements TelemetryEvent {
  readonly eventProperties: {
    view_link_id: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_commenting_displayed";

  constructor(props: { viewLinkId: string; webExportId?: string }) {
    this.eventProperties = {
      view_link_id: props.viewLinkId,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param viewLinkId
 * @param visible
 */
export class WebViewerCommentingSidebarVisibilityChanged implements TelemetryEvent {
  readonly eventProperties: {
    view_link_id: string;
    visible: boolean;
  };
  readonly eventName = "web_viewer_commenting_sidebar_visibility_changed";

  constructor(props: { viewLinkId: string; visible: boolean }) {
    this.eventProperties = {
      view_link_id: props.viewLinkId,
      visible: props.visible,
    };
  }
}

/**
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerComplexModelWarningShown implements TelemetryEvent {
  readonly eventProperties: {
    view_link_id: string;
    web_export_id: string;
  };
  readonly eventName = "web_viewer_complex_model_warning_shown";

  constructor(props: { viewLinkId: string; webExportId: string }) {
    this.eventProperties = {
      view_link_id: props.viewLinkId,
      web_export_id: props.webExportId,
    };
  }
}

/**
 * @param drawingId
 * @param viewLinkId
 */
export class WebViewerDrawingDisplayFailed implements TelemetryEvent {
  readonly eventProperties: {
    drawing_id?: string;
    view_link_id?: string;
  };
  readonly eventName = "web_viewer_drawing_display_failed";

  constructor(props: { drawingId?: string; viewLinkId?: string }) {
    this.eventProperties = {
      drawing_id: props.drawingId ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
    };
  }
}

/**
 * @param drawingId
 * @param durationMs
 * @param viewLinkId
 */
export class WebViewerDrawingDisplayed implements TelemetryEvent {
  readonly eventProperties: {
    drawing_id?: string;
    duration_ms?: number;
    view_link_id?: string;
  };
  readonly eventName = "web_viewer_drawing_displayed";

  constructor(props: { drawingId?: string; durationMs?: number; viewLinkId?: string }) {
    this.eventProperties = {
      drawing_id: props.drawingId ?? undefined,
      duration_ms: props.durationMs ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
    };
  }
}

/**
 * @param drawingId
 * @param viewLinkId
 */
export class WebViewerDrawingDownloadFailed implements TelemetryEvent {
  readonly eventProperties: {
    drawing_id?: string;
    view_link_id?: string;
  };
  readonly eventName = "web_viewer_drawing_download_failed";

  constructor(props: { drawingId?: string; viewLinkId?: string }) {
    this.eventProperties = {
      drawing_id: props.drawingId ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
    };
  }
}

/**
 * @param drawingId
 * @param durationMs
 * @param viewLinkId
 */
export class WebViewerDrawingDownloaded implements TelemetryEvent {
  readonly eventProperties: {
    drawing_id?: string;
    duration_ms?: number;
    view_link_id?: string;
  };
  readonly eventName = "web_viewer_drawing_downloaded";

  constructor(props: { drawingId?: string; durationMs?: number; viewLinkId?: string }) {
    this.eventProperties = {
      drawing_id: props.drawingId ?? undefined,
      duration_ms: props.durationMs ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
    };
  }
}

/**
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerModelDisplayFailed implements TelemetryEvent {
  readonly eventProperties: {
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_model_display_failed";

  constructor(props: { viewLinkId?: string; webExportId?: string }) {
    this.eventProperties = {
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param durationMs
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerModelDisplayed implements TelemetryEvent {
  readonly eventProperties: {
    duration_ms?: number;
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_model_displayed";

  constructor(props: { durationMs?: number; viewLinkId?: string; webExportId?: string }) {
    this.eventProperties = {
      duration_ms: props.durationMs ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerModelDownloadFailed implements TelemetryEvent {
  readonly eventProperties: {
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_model_download_failed";

  constructor(props: { viewLinkId?: string; webExportId?: string }) {
    this.eventProperties = {
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerModelDownloadStarted implements TelemetryEvent {
  readonly eventProperties: {
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_model_download_started";

  constructor(props: { viewLinkId?: string; webExportId?: string }) {
    this.eventProperties = {
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param durationMs
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerModelDownloaded implements TelemetryEvent {
  readonly eventProperties: {
    duration_ms?: number;
    view_link_id?: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_model_downloaded";

  constructor(props: { durationMs?: number; viewLinkId?: string; webExportId?: string }) {
    this.eventProperties = {
      duration_ms: props.durationMs ?? undefined,
      view_link_id: props.viewLinkId ?? undefined,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param newValue
 * @param oldValue
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerShareModalGeneralPermissionChanged implements TelemetryEvent {
  readonly eventProperties: {
    new_value: string;
    old_value: string;
    view_link_id: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_share_modal_general_permission_changed";

  constructor(props: {
    newValue: string;
    oldValue: string;
    viewLinkId: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      new_value: props.newValue,
      old_value: props.oldValue,
      view_link_id: props.viewLinkId,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param newValue
 * @param oldValue
 * @param viewLinkId
 * @param webExportId
 */
export class WebViewerShareModalGeneralRoleChanged implements TelemetryEvent {
  readonly eventProperties: {
    new_value: string;
    old_value: string;
    view_link_id: string;
    web_export_id?: string;
  };
  readonly eventName = "web_viewer_share_modal_general_role_changed";

  constructor(props: {
    newValue: string;
    oldValue: string;
    viewLinkId: string;
    webExportId?: string;
  }) {
    this.eventProperties = {
      new_value: props.newValue,
      old_value: props.oldValue,
      view_link_id: props.viewLinkId,
      web_export_id: props.webExportId ?? undefined,
    };
  }
}

/**
 * @param reason
 * @param viewLinkId
 */
export class WebViewerViewLinkAccessDenied implements TelemetryEvent {
  readonly eventProperties: {
    reason: "incorrect_password" | "unauthenticated" | "missing_access" | "missing_permission";
    view_link_id: string;
  };
  readonly eventName = "web_viewer_view_link_access_denied";

  constructor(props: {
    reason: "incorrect_password" | "unauthenticated" | "missing_access" | "missing_permission";
    viewLinkId: string;
  }) {
    this.eventProperties = {
      reason: props.reason,
      view_link_id: props.viewLinkId,
    };
  }
}

/** @param reason */
export class WelcomeModalClosed implements TelemetryEvent {
  readonly eventProperties: {
    reason: "download" | "skip";
  };
  readonly eventName = "welcome_modal_closed";

  constructor(props: { reason: "download" | "skip" }) {
    this.eventProperties = {
      reason: props.reason,
    };
  }
}

/** */
export class WelcomeModalShown implements TelemetryEvent {
  readonly eventProperties: Record<string, never>;
  readonly eventName = "welcome_modal_shown";

  constructor(props: Record<string, never> = {}) {
    this.eventProperties = props;
  }
}
